import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { parseReactProps } from '~/components/legacy/reactProps.js';
import {
  allQuestionsAnswered as projectLicenseAllQuestionsAnswered,
  buildConfigurationPayload as buildProjectLicenseConfigurationPayload,
  findSelectedOption as projectLicenseFindSelectedOption,
  formatProjectLicensePrice,
  getCoverageScenarioStatus as projectLicenseCoverageScenarioStatus,
  getEstimatedPriceCents as projectLicenseEstimatedPriceCents,
  getMaxUnlockedWizardStepIndex as projectLicenseMaxUnlockedWizardStepIndex,
  getSelectedOptionKeys as projectLicenseSelectedOptionKeys,
  getSelectedOptions as projectLicenseSelectedOptions,
  getVisibleQuestions as projectLicenseVisibleQuestions,
  isMatrixFamily as projectLicenseIsMatrixFamily,
  matrixBasePriceCents as projectLicenseMatrixBasePriceCents,
  normalizeScenario as normalizeProjectLicenseScenario,
  optionPriceLabel as projectLicenseOptionPriceLabel,
  optionQuestionMap as projectLicenseOptionQuestionMap,
  questionIndex as projectLicenseQuestionIndex,
  selectedOrDefaultOptionKey as projectLicenseSelectedOrDefaultOptionKey,
  additiveOptionTotalCents as projectLicenseAdditiveOptionTotalCents
} from '~/components/legacy/projectLicenseWizard.js';

class InlineLicenseConfig extends Component {
  constructor(props) {
    super(props);

    this.state = {
      expandedFamilies: props.defaultExpandedFamilyKeys || [],
      expandedCoverageFamilies: [],
      answersByFamily: props.initialAnswersByFamily || {},
      wizardStepByFamily: props.initialWizardStepByFamily || {}
    };
  }

  componentDidMount() {
    this.emitWizardStateChange();
  }

  componentDidUpdate(_prevProps, prevState) {
    if (
      JSON.stringify(prevState.answersByFamily) !== JSON.stringify(this.state.answersByFamily) ||
      JSON.stringify(prevState.wizardStepByFamily) !== JSON.stringify(this.state.wizardStepByFamily)
    ) {
      this.emitWizardStateChange();
    }
  }

  toggleFamily(familyKey) {
    if (this.forceExpandedFamilies().includes(familyKey)) {
      return;
    }

    const expandedFamilies = this.state.expandedFamilies.includes(familyKey) ?
      this.state.expandedFamilies.filter((key) => key !== familyKey) :
      [...this.state.expandedFamilies, familyKey];

    this.setState({ expandedFamilies: expandedFamilies });
  }

  resetFamily(familyKey) {
    this.setState({
      answersByFamily: {
        ...this.state.answersByFamily,
        [familyKey]: {}
      },
      wizardStepByFamily: {
        ...this.state.wizardStepByFamily,
        [familyKey]: 0
      }
    });
  }

  toggleCoverage(familyKey) {
    const expandedCoverageFamilies = this.state.expandedCoverageFamilies.includes(familyKey) ?
      this.state.expandedCoverageFamilies.filter((key) => key !== familyKey) :
      [...this.state.expandedCoverageFamilies, familyKey];

    this.setState({ expandedCoverageFamilies: expandedCoverageFamilies });
  }

  handleOptionSelect(familyKey, questionKey, optionKey) {
    const currentAnswers = this.state.answersByFamily[familyKey] || {};
    const family = this.findFamilyByKey(familyKey);
    const currentQuestionIndex = family ? this.questionIndex(family, questionKey) : -1;
    const nextAnswers = {
      ...currentAnswers,
      [questionKey]: optionKey
    };
    const nextState = {
      answersByFamily: {
        ...this.state.answersByFamily,
        [familyKey]: nextAnswers
      }
    };

    if (this.isWizardMode() && family) {
      const currentQuestionIndex = this.questionIndex(family, questionKey);
      const maxUnlockedQuestionIndex = this.getMaxUnlockedWizardStepIndex(family, nextAnswers);
      const nextWizardStepIndex = currentQuestionIndex < maxUnlockedQuestionIndex ? currentQuestionIndex + 1 : currentQuestionIndex;

      nextState.wizardStepByFamily = {
        ...this.state.wizardStepByFamily,
        [familyKey]: nextWizardStepIndex
      };
    }

    this.setState(nextState, () => {
      if (this.shouldAutoSubmitWizardResult(family, nextAnswers, currentQuestionIndex)) {
        this.props.onResultAction(this.buildConfigurationPayload(family));
      }
    });
  }

  isExpanded(familyKey) {
    return this.forceExpandedFamilies().includes(familyKey) || this.state.expandedFamilies.includes(familyKey);
  }

  forceExpandedFamilies() {
    return Array.isArray(this.props.forceExpandedFamilyKeys) ? this.props.forceExpandedFamilyKeys : [];
  }

  getAnswersForFamily(familyKey) {
    return this.state.answersByFamily[familyKey] || {};
  }

  isCoverageExpanded(familyKey) {
    return this.state.expandedCoverageFamilies.includes(familyKey);
  }

  getVisibleQuestions(family) {
    return projectLicenseVisibleQuestions(family, this.getAnswersForFamily(family.key));
  }

  isWizardMode() {
    return this.props.display_mode === 'wizard';
  }

  isQuestionsFirstLayout() {
    return this.props.panel_layout === 'questions_first';
  }

  isLiveSummaryLayout() {
    return this.props.panel_layout === 'questions_with_live_summary';
  }

  hasWizardResultStep() {
    return this.props.skipWizardResultStep !== true;
  }

  shouldAutoSubmitWizardResult(family, answers, currentQuestionIndex) {
    return Boolean(
      this.isWizardMode() &&
      family &&
      !this.hasWizardResultStep() &&
      typeof this.props.onResultAction === 'function' &&
      this.allQuestionsAnsweredWithAnswers(family, answers) &&
      currentQuestionIndex >= family.questions.length - 1
    );
  }

  findFamilyByKey(familyKey) {
    return this.props.families.find((family) => family.key === familyKey) || null;
  }

  questionIndex(family, questionKey) {
    return projectLicenseQuestionIndex(family, questionKey);
  }

  getFirstUnansweredQuestionIndex(family, answers = this.getAnswersForFamily(family.key)) {
    return family.questions.findIndex((question) => !answers[question.key]);
  }

  getMaxUnlockedWizardStepIndex(family, answers = this.getAnswersForFamily(family.key)) {
    return projectLicenseMaxUnlockedWizardStepIndex(
      family,
      answers,
      { skipWizardResultStep: !this.hasWizardResultStep() }
    );
  }

  getWizardStepIndex(family) {
    const configuredStepIndex = this.state.wizardStepByFamily[family.key];
    const fallbackStepIndex = this.getMaxUnlockedWizardStepIndex(family);

    if (typeof configuredStepIndex !== 'number') {
      return fallbackStepIndex;
    }

    return Math.min(configuredStepIndex, fallbackStepIndex);
  }

  goToWizardStep(familyKey, nextIndex) {
    const family = this.findFamilyByKey(familyKey);

    if (!family) {
      return;
    }

    const maxUnlockedQuestionIndex = this.getMaxUnlockedWizardStepIndex(family);
    const clampedStepIndex = Math.max(0, Math.min(nextIndex, maxUnlockedQuestionIndex));

    this.setState({
      wizardStepByFamily: {
        ...this.state.wizardStepByFamily,
        [familyKey]: clampedStepIndex
      }
    });
  }

  emitWizardStateChange() {
    if (!this.isWizardMode() || typeof this.props.onWizardStateChange !== 'function') {
      return;
    }

    const family = this.props.families[0];

    if (!family) {
      return;
    }

    const currentQuestionIndex = this.getWizardStepIndex(family);
    const isResultStep = this.hasWizardResultStep() && currentQuestionIndex === family.questions.length;
    const displayStep = isResultStep ? family.questions.length + 1 : currentQuestionIndex + 1;

    this.props.onWizardStateChange({
      familyKey: family.key,
      questionCount: family.questions.length,
      currentStepIndex: currentQuestionIndex,
      displayStep: displayStep,
      isResultStep: isResultStep
    });
  }

  findSelectedOption(question, answerKey) {
    return projectLicenseFindSelectedOption(question, answerKey);
  }

  getSelectedOptions(family) {
    return projectLicenseSelectedOptions(family, this.getAnswersForFamily(family.key));
  }

  getSelectedOptionKeys(family) {
    return projectLicenseSelectedOptionKeys(family, this.getAnswersForFamily(family.key));
  }

  findQuestionByKey(family, questionKey) {
    return family.questions.find((question) => question.key === questionKey) || null;
  }

  selectedOrDefaultOptionKey(family, questionKey, answers = this.getAnswersForFamily(family.key)) {
    return projectLicenseSelectedOrDefaultOptionKey(family, questionKey, answers);
  }

  isMatrixFamily(family) {
    return projectLicenseIsMatrixFamily(family);
  }

  matrixBasePriceCents(family, answers = this.getAnswersForFamily(family.key)) {
    return projectLicenseMatrixBasePriceCents(family, answers);
  }

  additiveOptionTotalCents(family, answers = this.getAnswersForFamily(family.key)) {
    return projectLicenseAdditiveOptionTotalCents(family, answers);
  }

  optionQuestionMap(family) {
    return projectLicenseOptionQuestionMap(family);
  }

  allQuestionsAnswered(family) {
    return projectLicenseAllQuestionsAnswered(family, this.getAnswersForFamily(family.key));
  }

  allQuestionsAnsweredWithAnswers(family, answers) {
    return projectLicenseAllQuestionsAnswered(family, answers);
  }

  getEstimatedPriceCents(family) {
    return projectLicenseEstimatedPriceCents(family, this.getAnswersForFamily(family.key));
  }

  formatPrice(cents) {
    return formatProjectLicensePrice(cents, this.props.locale);
  }

  optionPriceLabel(family, question, option) {
    return projectLicenseOptionPriceLabel(
      family,
      question,
      option,
      this.getAnswersForFamily(family.key),
      this.props.locale
    );
  }

  normalizeScenario(scenario) {
    return normalizeProjectLicenseScenario(scenario);
  }

  getCoverageScenarioStatus(family, rawScenario) {
    return projectLicenseCoverageScenarioStatus(family, rawScenario, this.getAnswersForFamily(family.key));
  }

  coverageScenarioStatusLabel(status) {
    const labels = this.props.i18n.coverage_status_labels || InlineLicenseConfig.defaultProps.i18n.coverage_status_labels;

    return labels[status] || '';
  }

  i18nValue(key) {
    if (this.props.i18n && this.props.i18n[key] !== undefined) {
      return this.props.i18n[key];
    }

    return InlineLicenseConfig.defaultProps.i18n[key];
  }

  renderSummaryList(family) {
    const selectedOptions = this.getSelectedOptions(family);

    if (selectedOptions.length === 0) {
      return (
        <div className='inline-license-config__empty-copy'>
          {this.props.i18n.start_hint}
        </div>
      );
    }

    return (
      <ul className='inline-license-config__summary-list'>
        {selectedOptions.map((option) => (
          <li className='inline-license-config__summary-item' key={option.key}>
            <span className='inline-license-config__summary-label'>{option.label}</span>
            <span className='inline-license-config__summary-copy'>{option.summary}</span>
          </li>
        ))}
      </ul>
    );
  }

  renderCoverageSection(family, forceExpanded = false) {
    if (!family.covered_scenarios || family.covered_scenarios.length === 0) {
      return null;
    }

    const expanded = forceExpanded || this.isCoverageExpanded(family.key);

    return (
      <div className='inline-license-config__coverage'>
        <button
          type='button'
          className={expanded ? 'inline-license-config__coverage-toggle inline-license-config__coverage-toggle--expanded' : 'inline-license-config__coverage-toggle'}
          onClick={() => this.toggleCoverage(family.key)}
        >
          <span>{this.props.i18n.covered_scenarios}</span>
          <span className='inline-license-config__coverage-toggle-icon'>{expanded ? '−' : '+'}</span>
        </button>
        {expanded ? (
          <div className='inline-license-config__coverage-panel'>
            <ul className='inline-license-config__coverage-list'>
              {family.covered_scenarios.map((rawScenario) => {
                const scenario = this.normalizeScenario(rawScenario);
                const status = this.getCoverageScenarioStatus(family, scenario);

                return (
                  <li
                    className={`inline-license-config__coverage-item inline-license-config__coverage-item--${status}`}
                    key={scenario.title}
                  >
                    <div className='inline-license-config__coverage-item-row'>
                      <span>{scenario.title}</span>
                      {this.coverageScenarioStatusLabel(status).length > 0 ? (
                        <span className={`inline-license-config__coverage-badge inline-license-config__coverage-badge--${status}`}>
                          {this.coverageScenarioStatusLabel(status)}
                        </span>
                      ) : null}
                    </div>
                    {scenario.note ? (
                      <div className='inline-license-config__coverage-item-note'>{scenario.note}</div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
            {family.covered_scenarios_note ? (
              <p className='inline-license-config__coverage-note'>{family.covered_scenarios_note}</p>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  renderQuestion(family, question, index) {
    const answers = this.getAnswersForFamily(family.key);
    const selectedOption = this.findSelectedOption(question, answers[question.key]);

    return (
      <div className='inline-license-config__question' key={question.key}>
        <div className='inline-license-config__question-meta'>
          <span className='inline-license-config__question-step'>
            {index + 1}
          </span>
          <div>
            <div className='inline-license-config__question-title'>{question.title}</div>
            {selectedOption ? (
              <div className='inline-license-config__question-selection'>{selectedOption.summary}</div>
            ) : (
              <div className='inline-license-config__question-selection inline-license-config__question-selection--muted'>{this.props.i18n.choose_option}</div>
            )}
          </div>
        </div>
        <div className='inline-license-config__option-row'>
          {question.options.map((option) => {
            const selected = answers[question.key] === option.key;

            return (
              <button
                type='button'
                className={selected ? 'inline-license-config__option inline-license-config__option--selected' : 'inline-license-config__option'}
                key={option.key}
                onClick={() => this.handleOptionSelect(family.key, question.key, option.key)}
              >
                <span className='inline-license-config__option-label'>{option.label}</span>
                {this.optionPriceLabel(family, question, option) ? (
                  <span className='inline-license-config__option-price'>{this.optionPriceLabel(family, question, option)}</span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  renderWizardFlow(family) {
    const visibleQuestions = this.getVisibleQuestions(family);
    const currentQuestionIndex = this.getWizardStepIndex(family);
    const isResultStep = this.hasWizardResultStep() && currentQuestionIndex === family.questions.length;
    const currentQuestion = isResultStep ? null : visibleQuestions[currentQuestionIndex];
    const answers = this.getAnswersForFamily(family.key);
    const currentAnswerSelected = currentQuestion ? Boolean(answers[currentQuestion.key]) : false;
    const isFirstQuestion = currentQuestionIndex === 0;
    const canAdvance = !isResultStep && currentAnswerSelected && currentQuestionIndex < this.getMaxUnlockedWizardStepIndex(family);
    const progressPercent = isResultStep ? 100 : ((currentQuestionIndex + 1) / family.questions.length) * 100;
    const showBackButton = this.props.showWizardBackButton !== false && !isFirstQuestion;
    const showNextButton = this.props.showWizardNextButton !== false && !isResultStep;
    const showEditButton = this.props.showWizardEditButton !== false && isResultStep;
    const showWizardActions = showBackButton || showNextButton || showEditButton;

    return (
      <div className='inline-license-config__flow-panel inline-license-config__flow-panel--wizard'>
        {this.props.showFlowHeading !== false ? (
          <div className='inline-license-config__flow-heading'>{this.props.i18n.final_scope}</div>
        ) : null}
        {this.props.showWizardProgress !== false ? (
          <div className='inline-license-config__wizard-progress'>
            <span className='inline-license-config__wizard-progress-copy'>
              {isResultStep ? this.i18nValue('result_step') : `${currentQuestionIndex + 1} / ${family.questions.length}`}
            </span>
            {this.props.showProgressBar !== false ? (
              <div className='inline-license-config__wizard-progress-track'>
                <div className='inline-license-config__wizard-progress-fill' style={{ width: `${progressPercent}%` }}></div>
              </div>
            ) : null}
          </div>
        ) : null}
        <div className='inline-license-config__flow-questions inline-license-config__flow-questions--wizard'>
          {isResultStep ? this.renderWizardResult(family) : this.renderQuestion(family, currentQuestion, currentQuestionIndex)}
        </div>
        {showWizardActions ? (
          <div className='inline-license-config__wizard-actions'>
            {showBackButton ? (
              <button
                type='button'
                className='inline-license-config__wizard-button inline-license-config__wizard-button--secondary'
                onClick={() => this.goToWizardStep(family.key, currentQuestionIndex - 1)}
              >
                {this.i18nValue('back')}
              </button>
            ) : null}
            {showEditButton ? (
              <button
                type='button'
                className='inline-license-config__wizard-button'
                onClick={() => this.goToWizardStep(family.key, family.questions.length - 1)}
              >
                {this.i18nValue('edit_answers')}
              </button>
            ) : null}
            {showNextButton ? (
              <button
                type='button'
                className='inline-license-config__wizard-button'
                disabled={!canAdvance}
                onClick={() => this.goToWizardStep(family.key, currentQuestionIndex + 1)}
              >
                {this.i18nValue('next')}
              </button>
            ) : null}
          </div>
        ) : null}
        {this.allQuestionsAnswered(family) && this.props.showWizardCompleteBadge !== false ? (
          <div className='inline-license-config__complete-badge'>{this.props.i18n.done}</div>
        ) : null}
      </div>
    );
  }

  renderWizardResult(family) {
    const resultActionLabel = this.props.resultActionLabel || this.i18nValue('result_action');

    return (
      <div className='inline-license-config__wizard-result'>
        <div className='inline-license-config__wizard-result-title'>{this.i18nValue('result_title')}</div>
        <div className='inline-license-config__wizard-result-copy'>{this.i18nValue('result_copy')}</div>
        <div className='inline-license-config__wizard-result-price'>
          <span className='inline-license-config__wizard-result-price-label'>{this.props.i18n.estimated_price}</span>
          <strong>{this.formatPrice(this.getEstimatedPriceCents(family))}</strong>
        </div>
        <div className='inline-license-config__wizard-result-summary'>
          <div className='inline-license-config__summary-heading'>{this.props.i18n.selected_answers}</div>
          {this.renderSummaryList(family)}
        </div>
        {this.renderCoverageSection(family, true)}
        {typeof this.props.onResultAction === 'function' ? (
          <div className='inline-license-config__wizard-result-actions'>
            <button
              type='button'
              className='inline-license-config__wizard-button'
              onClick={() => this.props.onResultAction(this.buildConfigurationPayload(family))}
            >
              {resultActionLabel}
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  buildConfigurationPayload(family) {
    return buildProjectLicenseConfigurationPayload(family, this.getAnswersForFamily(family.key));
  }

  renderStackedFlow(family) {
    const visibleQuestions = this.getVisibleQuestions(family);

    return (
      <div className='inline-license-config__flow-panel'>
        <div className='inline-license-config__flow-heading'>{this.props.i18n.final_scope}</div>
        <div className='inline-license-config__flow-questions'>
          {visibleQuestions.map((question, index) => this.renderQuestion(family, question, index))}
        </div>
        {this.allQuestionsAnswered(family) ? (
          <div className='inline-license-config__complete-badge'>{this.props.i18n.done}</div>
        ) : null}
      </div>
    );
  }

  renderSummaryPanel(family, estimatedPrice, complete, { liveSummary = false } = {}) {
    return (
      <div className={liveSummary ? 'inline-license-config__summary-panel inline-license-config__summary-panel--live' : 'inline-license-config__summary-panel'}>
        {liveSummary ? (
          <div className='inline-license-config__summary-live-header'>
            <div className='inline-license-config__summary-live-label'>{this.i18nValue('live_summary')}</div>
            <div className='inline-license-config__summary-live-title'>{this.i18nValue('live_summary_title')}</div>
            <p className='inline-license-config__summary-live-copy'>{this.i18nValue('live_summary_copy')}</p>
          </div>
        ) : null}
        <div className='inline-license-config__summary-grid'>
          <div className='inline-license-config__summary-stat'>
            <span className='inline-license-config__summary-stat-label'>{this.props.i18n.base_price}</span>
            <strong>{this.formatPrice(family.gross_price_cents)}</strong>
          </div>
          <div className='inline-license-config__summary-stat'>
            <span className='inline-license-config__summary-stat-label'>{this.props.i18n.estimated_price}</span>
            <strong className='inline-license-config__summary-price'>{this.formatPrice(estimatedPrice)}</strong>
          </div>
        </div>

        <div className='inline-license-config__summary-heading'>{this.props.i18n.selected_answers}</div>
        {this.renderSummaryList(family)}
        {this.renderCoverageSection(family)}

        {liveSummary ? null : (
          <div className='inline-license-config__summary-footer'>
            <div className='inline-license-config__summary-footer-title'>
              {complete ? this.props.i18n.finished : this.props.i18n.next_step}
            </div>
            <div className='inline-license-config__summary-footer-copy'>
              {complete ? this.props.i18n.try_cart_copy : this.props.i18n.more_questions}
            </div>
          </div>
        )}
      </div>
    );
  }

  renderFamilyCard(family) {
    const expanded = this.isExpanded(family.key);
    const selectedOptions = this.getSelectedOptions(family);
    const complete = this.allQuestionsAnswered(family);
    const estimatedPrice = this.getEstimatedPriceCents(family);
    const showCustomizeLink = this.props.showCustomizeLink !== false;
    const showResetLink = this.props.showResetLink !== false;
    const liveSummaryLayout = this.isWizardMode() && this.isLiveSummaryLayout();
    const questionsFirstLayout = this.isWizardMode() && this.isQuestionsFirstLayout();
    const familyBodyClassName = liveSummaryLayout ?
      'inline-license-config__family-body inline-license-config__family-body--live-summary' :
      questionsFirstLayout ?
      'inline-license-config__family-body inline-license-config__family-body--questions-first' :
      'inline-license-config__family-body';
    const flowMarkup = expanded ? (this.isWizardMode() ? this.renderWizardFlow(family) : this.renderStackedFlow(family)) : null;
    const summaryMarkup = this.renderSummaryPanel(family, estimatedPrice, complete, { liveSummary: liveSummaryLayout });
    const headerAction = this.props.headerAction;
    const familyHeaderMarkup = (
      <div className={liveSummaryLayout ? 'inline-license-config__family-header inline-license-config__family-header--live-summary' : 'inline-license-config__family-header'}>
        <div>
          {this.props.familyHeaderEyebrow ? (
            <div className='inline-license-config__family-eyebrow'>{this.props.familyHeaderEyebrow}</div>
          ) : null}
          <div className='inline-license-config__family-title-row'>
            <h2 className='inline-license-config__family-title'>{family.title}</h2>
            <span className='inline-license-config__family-price'>ab {this.formatPrice(family.gross_price_cents)}</span>
          </div>
          <p className='inline-license-config__family-description'>{family.description}</p>
        </div>
        <div className='inline-license-config__family-links'>
          {headerAction && typeof headerAction.onClick === 'function' ? (
            <button
              type='button'
              className={headerAction.className || 'inline-license-config__reset-link'}
              onClick={headerAction.onClick}
            >
              {headerAction.label}
            </button>
          ) : null}
          {showCustomizeLink ? (
            <button type='button' className='inline-license-config__customize-link' onClick={() => this.toggleFamily(family.key)}>
              {this.props.i18n.customize}
            </button>
          ) : null}
          {showResetLink && selectedOptions.length > 0 ? (
            <button type='button' className='inline-license-config__reset-link' onClick={() => this.resetFamily(family.key)}>
              {this.props.i18n.reset}
            </button>
          ) : null}
        </div>
      </div>
    );
    const familyCardClassName = [
      'inline-license-config__family-card',
      this.props.familyHeaderEyebrow ? 'inline-license-config__family-card--with-eyebrow' : null,
      this.props.familyHeaderEyebrowMobileOnly ? 'inline-license-config__family-card--eyebrow-mobile-only' : null
    ].filter(Boolean).join(' ');
    const familyFooter = typeof this.props.renderFamilyFooter === 'function' ? this.props.renderFamilyFooter(
      family,
      {
        answers: this.getAnswersForFamily(family.key),
        complete: complete,
        configuration: this.buildConfigurationPayload(family),
        expanded: expanded,
        estimatedPriceCents: estimatedPrice,
        selectedOptionKeys: this.getSelectedOptionKeys(family),
        toggleFamily: () => this.toggleFamily(family.key)
      }
    ) : null;

    return (
      <section className={familyCardClassName} key={family.key}>
        {liveSummaryLayout ? null : familyHeaderMarkup}

        <div className={familyBodyClassName}>
          {liveSummaryLayout ? (
            <>
              <div className='inline-license-config__family-column inline-license-config__family-column--flow'>
                {flowMarkup}
              </div>
              <div className='inline-license-config__family-column inline-license-config__family-column--summary'>
                {familyHeaderMarkup}
                {summaryMarkup}
              </div>
            </>
          ) : (
            <>
              {questionsFirstLayout ? flowMarkup : summaryMarkup}
              {questionsFirstLayout ? summaryMarkup : flowMarkup}
            </>
          )}
        </div>
        {familyFooter}
      </section>
    );
  }

  render() {
    return (
      <div className='inline-license-config'>
        {this.props.showIntro !== false ? (
          <div className='inline-license-config__intro'>
            <h2 className='inline-license-config__heading'>{this.props.i18n.heading}</h2>
            <p className='inline-license-config__subheading'>{this.props.i18n.subheading}</p>
          </div>
        ) : null}
        <div className='inline-license-config__grid'>
          {this.props.families.map((family) => this.renderFamilyCard(family))}
        </div>
      </div>
    );
  }
}

InlineLicenseConfig.defaultProps = {
  locale: 'de',
  display_mode: 'stacked',
  panel_layout: 'side_by_side',
  showIntro: true,
  defaultExpandedFamilyKeys: [],
  forceExpandedFamilyKeys: [],
  showCustomizeLink: true,
  showResetLink: true,
  headerAction: null,
  showProgressBar: true,
  showWizardProgress: true,
  showFlowHeading: true,
  showWizardBackButton: true,
  showWizardNextButton: true,
  showWizardEditButton: true,
  showWizardCompleteBadge: true,
  onWizardStateChange: null,
  onResultAction: null,
  resultActionLabel: null,
  skipWizardResultStep: false,
  familyHeaderEyebrow: null,
  familyHeaderEyebrowMobileOnly: false,
  initialAnswersByFamily: {},
  initialWizardStepByFamily: {},
  families: [],
  renderFamilyFooter: null,
  i18n: {
    heading: 'Lizenz-Quick-Config',
    subheading: '',
    customize: 'Lizenz für Ihr Projekt anpassen',
    reset: 'Zurücksetzen',
    covered_scenarios: 'Abgedeckte Nutzungsszenarien',
    selected_answers: 'Ausgewählte Bausteine',
    next_step: 'Nächster sinnvoller Schritt',
    final_scope: 'Aktueller Demo-Scope',
    base_price: 'Basispreis',
    estimated_price: 'Demo-Preis',
    finished: 'Quick-Config abgeschlossen',
    start_hint: '',
    try_cart_copy: '',
    choose_option: '',
    more_questions: '',
    done: 'Fertig konfiguriert',
    back: 'Zurück',
    next: 'Weiter',
    result_step: 'Ergebnis',
    result_title: 'Ihre Lizenz-Konfiguration ist fertig',
    result_copy: 'Hier sehen Sie den aktuellen Scope mit Preis und abgedeckten Nutzungsszenarien.',
    edit_answers: 'Antworten ändern',
    result_action: 'Konfiguration übernehmen',
    live_summary: 'Aktueller Stand',
    live_summary_title: 'Aktueller Stand für Ihr Projekt',
    live_summary_copy: 'Hier sehen Sie laufend Preis, gewählte Bausteine und die aktuell abgedeckten Nutzungsszenarien.',
    coverage_status_labels: {
      covered: 'abgedeckt',
      not_covered: 'nicht abgedeckt',
      review: 'Sonderfall prüfen',
      open: ''
    }
  }
};

export function parseInlineLicenseConfigProps(rawProps) {
  return parseReactProps(rawProps);
}

let elements = document.querySelectorAll('[data-react-component=InlineLicenseConfig]');

elements.forEach(function(element) {
  try {
    let reactProps = parseInlineLicenseConfigProps(element.dataset.reactProps);

    ReactDOM.render(
      <React.StrictMode>
        <InlineLicenseConfig {...reactProps} />
      </React.StrictMode>,
      element
    );
  } catch (error) {
    console.error('Failed to mount InlineLicenseConfig', error);
  }
});

export default InlineLicenseConfig;
